// Lightroom `.xmp` preset import (§11.6). Reads the `crs:` attributes (attribute or element form) that
// have a counterpart here and returns a Preset plus a report of what was mapped and what was ignored.
// Lightroom's scales are not ours: the mapping is approximate, and the UI says so.
import { HUE_BANDS, type HueBand } from '../color/hsl';
import type { MessageKey } from '../i18n';
import { defaultSettings } from '../model/defaults';
import type { Preset, Pt, Settings, SyncGroup } from '../model/types';

/** One documented table: Lightroom key → our setting, with the scale factor applied (value × k). */
export const XMP_MAP: { key: string; group: SyncGroup; k: number; set: (s: Settings, v: number) => void }[] =
  [
    { key: 'Exposure2012', group: 'light', k: 1, set: (s, v) => void (s.light.exposure = clamp(v, -5, 5)) },
    { key: 'Contrast2012', group: 'light', k: 1, set: (s, v) => void (s.light.contrast = clamp100(v)) },
    { key: 'Highlights2012', group: 'light', k: 1, set: (s, v) => void (s.light.highlights = clamp100(v)) },
    { key: 'Shadows2012', group: 'light', k: 1, set: (s, v) => void (s.light.shadows = clamp100(v)) },
    { key: 'Whites2012', group: 'light', k: 1, set: (s, v) => void (s.light.whites = clamp100(v)) },
    { key: 'Blacks2012', group: 'light', k: 1, set: (s, v) => void (s.light.blacks = clamp100(v)) },
    { key: 'Vibrance', group: 'color', k: 1, set: (s, v) => void (s.color.vibrance = clamp100(v)) },
    { key: 'Saturation', group: 'color', k: 1, set: (s, v) => void (s.color.saturation = clamp100(v)) },
    { key: 'IncrementalTemperature', group: 'wb', k: 1, set: (s, v) => void (s.wb.temp = clamp100(v)) },
    { key: 'IncrementalTint', group: 'wb', k: 1, set: (s, v) => void (s.wb.tint = clamp100(v)) },
    {
      key: 'Sharpness',
      group: 'detail',
      k: 1,
      set: (s, v) => void (s.detail.sharpAmount = clamp(v, 0, 150)),
    },
    {
      key: 'SharpenRadius',
      group: 'detail',
      k: 1,
      set: (s, v) => void (s.detail.sharpRadius = clamp(v, 0.5, 3)),
    },
    {
      key: 'SharpenEdgeMasking',
      group: 'detail',
      k: 1,
      set: (s, v) => void (s.detail.sharpMasking = clamp(v, 0, 100)),
    },
    {
      key: 'LuminanceSmoothing',
      group: 'detail',
      k: 1,
      set: (s, v) => void (s.detail.nrLuma = clamp(v, 0, 100)),
    },
    {
      key: 'ColorNoiseReduction',
      group: 'detail',
      k: 1,
      set: (s, v) => void (s.detail.nrColor = clamp(v, 0, 100)),
    },
    ...HUE_BANDS.flatMap((band) => {
      const lr = band.charAt(0).toUpperCase() + band.slice(1);
      return (['Hue', 'Saturation', 'Luminance'] as const).map((kind) => ({
        key: `${kind}Adjustment${lr}`,
        group: 'hsl' as SyncGroup,
        k: 1,
        set: (s: Settings, v: number) =>
          void (s.hsl[band as HueBand][kind === 'Hue' ? 'h' : kind === 'Saturation' ? 's' : 'l'] =
            clamp100(v)),
      }));
    }),
  ];
const CURVES: { key: string; channel: keyof Settings['curve'] }[] = [
  { key: 'ToneCurvePV2012', channel: 'master' },
  { key: 'ToneCurvePV2012Red', channel: 'r' },
  { key: 'ToneCurvePV2012Green', channel: 'g' },
  { key: 'ToneCurvePV2012Blue', channel: 'b' },
];
/** Keys we recognise but deliberately do not apply, with the reason shown in the report. */
const KNOWN_IGNORED: Record<string, string> = {
  Temperature: 'absolute temperature (Kelvin) has no meaning for JPEGs here; use IncrementalTemperature',
  Tint: 'absolute tint is ignored; use IncrementalTint',
  WhiteBalance: 'white balance mode is not imported',
  Texture: 'no texture control',
  Clarity2012: 'no clarity control',
  Dehaze: 'no dehaze control',
  PostCropVignetteAmount: 'vignette is not imported',
  GrainAmount: 'grain is not imported',
};
const IGNORED_KEYS: Record<string, MessageKey> = {
  Temperature: 'xmpAbsTemp',
  Tint: 'xmpAbsTint',
  WhiteBalance: 'xmpWbMode',
  Texture: 'xmpNoTexture',
  Clarity2012: 'xmpNoClarity',
  Dehaze: 'xmpNoDehaze',
  PostCropVignetteAmount: 'xmpNoVignette',
  GrainAmount: 'xmpNoGrain',
};

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const clamp100 = (v: number) => clamp(v, -100, 100);

export type XmpReport = {
  name: string;
  /** `points`: a curve's point count (shown translated). */
  mapped: { key: string; value: number | string; to: string; points?: number }[];
  /** `reason` is English (tests, logs); the panel shows `reasonKey` translated (CODE_REVIEW S19). */
  ignored: { key: string; reason: string; reasonKey: MessageKey; vars?: Record<string, string> }[];
};

/** All crs: values in the document: attribute form `crs:X="v"` and element form `<crs:X>v</crs:X>`. */
function crsValues(xml: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const m of xml.matchAll(/crs:([A-Za-z0-9]+)\s*=\s*"([^"]*)"/g)) out.set(m[1]!, m[2]!);
  for (const m of xml.matchAll(/<crs:([A-Za-z0-9]+)>([^<]*)<\/crs:\1>/g)) out.set(m[1]!, m[2]!.trim());
  return out;
}

/** Tone curve points from `<crs:ToneCurvePV2012><rdf:Seq><rdf:li>x, y</rdf:li>…` (0..255). */
function curvePoints(xml: string, key: string): Pt[] | null {
  const m = new RegExp(`<crs:${key}>([\\s\\S]*?)</crs:${key}>`).exec(xml);
  if (!m) return null;
  const pts = [...m[1]!.matchAll(/<rdf:li>\s*([-\d.]+)\s*,\s*([-\d.]+)\s*<\/rdf:li>/g)].map((p) => ({
    x: clamp(Number(p[1]) / 255, 0, 1),
    y: clamp(Number(p[2]) / 255, 0, 1),
  }));
  return pts.length >= 2 ? pts : null;
}

const isIdentity = (pts: Pt[]) => pts.every((p) => Math.abs(p.x - p.y) < 1e-6);

export function importXmp(xml: string, fileName = 'preset.xmp'): { preset: Preset; report: XmpReport } {
  if (!/crs:|camera-raw-settings/i.test(xml)) throw new Error('not a Lightroom / Camera Raw preset');
  const values = crsValues(xml);
  const s = defaultSettings();
  const groups = new Set<SyncGroup>();
  const nameMatch =
    /<crs:Name>\s*<rdf:Alt>\s*<rdf:li[^>]*>([^<]*)<\/rdf:li>/.exec(xml) ??
    /crs:PresetName="([^"]*)"/.exec(xml);
  const name = nameMatch?.[1]?.trim() || fileName.replace(/\.xmp$/i, '');
  const report: XmpReport = { name, mapped: [], ignored: [] };
  const handled = new Set<string>();
  for (const m of XMP_MAP) {
    const raw = values.get(m.key);
    if (raw === undefined) continue;
    handled.add(m.key);
    const v = Number(raw.replace(/^\+/, ''));
    if (!Number.isFinite(v)) {
      report.ignored.push({
        key: m.key,
        reason: `not a number: "${raw}"`,
        reasonKey: 'xmpNotNumber',
        vars: { raw },
      });
      continue;
    }
    if (v === 0) continue; // zero is our default; nothing to report
    m.set(s, v * m.k);
    groups.add(m.group);
    report.mapped.push({ key: m.key, value: v, to: m.group });
  }
  for (const c of CURVES) {
    const pts = curvePoints(xml, c.key);
    handled.add(c.key);
    if (!pts) continue;
    if (isIdentity(pts)) continue;
    s.curve[c.channel] = pts;
    groups.add('curve');
    report.mapped.push({ key: c.key, value: `${pts.length} points`, to: 'curve', points: pts.length });
  }
  for (const [key, raw] of values) {
    if (handled.has(key)) continue;
    if (KNOWN_IGNORED[key]) {
      if (raw !== '0' && raw !== '+0')
        report.ignored.push({ key, reason: KNOWN_IGNORED[key]!, reasonKey: IGNORED_KEYS[key]! });
      continue;
    }
    // Housekeeping fields (version, names, UUIDs, flags) are not settings.
    if (
      /^(Version|ProcessVersion|PresetType|Cluster|UUID|SupportsAmount|SupportsColor|SupportsMonochrome|SupportsHighDynamicRange|SupportsNormalDynamicRange|SupportsSceneReferred|SupportsOutputReferred|CameraModelRestriction|Copyright|ContactInfo|HasSettings|ConvertToGrayscale|Name|ShortName|SortName|Group|Description|PresetName)$/.test(
        key,
      )
    )
      continue;
    report.ignored.push({ key, reason: 'no matching control', reasonKey: 'xmpNoControl' });
  }
  const settings: Partial<Settings> = {};
  for (const g of groups) {
    if (g === 'light') settings.light = s.light;
    if (g === 'wb') settings.wb = s.wb;
    if (g === 'color') settings.color = s.color;
    if (g === 'hsl') settings.hsl = s.hsl;
    if (g === 'curve') settings.curve = s.curve;
    if (g === 'detail') settings.detail = s.detail;
  }
  const preset: Preset = {
    id: `xmp-${Date.now().toString(36)}`,
    name,
    builtIn: false,
    groups: [...groups],
    settings,
  };
  return { preset, report };
}
